import React, { useEffect, useState } from "react";
import { pluralize } from "../../../other/utils/otherUtils.jsx";
import { BryntumButton } from "@bryntum/schedulerpro-react";
import { AnimatePresence, motion } from "framer-motion";
import { defaultCellRenderer, getAging, linkifyText } from "../../../other/utils/utils.jsx";
import { DateHelper } from "@bryntum/schedulerpro";
import { ALERT_TYPE_NAMES } from "../../../other/constants/OtherKeys.jsx";

const AlertBoardUserNames = ({ unseenBoardAlertsOfMine }) => {
  const boardUserNames = unseenBoardAlertsOfMine.reduce((nonSeenAlerts, alert) => {
    if (!alert.isSeen && alert.creatorBoardUserRecord?.name) {
      nonSeenAlerts.push(alert.creatorBoardUserRecord.name);
    }
    return nonSeenAlerts;
  }, []);

  const uniqueCreators = boardUserNames
    .filter((alert, i, self) => i === self.findIndex((t) => t === alert))
    .map((a) => ({
      name: a,
      count: boardUserNames.filter((b) => b === a).length
    }));

  return (
    <>
      {uniqueCreators.length > 0 && (
        <div>
          <p className="flex-wrap gap-x-8 inline-flex gap-y-[14px] text-sm mt-0">
            {uniqueCreators.map((a) => (
              <span key={a.name} className="relative">
                {a.name}
                <span
                  className={`absolute left-full inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] text-xs font-bold border-solid border-2 rounded-full border-[#d0d0d0] text-[#d0d0d0] dark:text-[#737373] dark:border-[#4d4d4d] top-[-15px] scale-75 ml-1`}
                >
                  {a.count}
                </span>
              </span>
            ))}
          </p>
        </div>
      )}
    </>
  );
};

const AlertCard = ({ alert, unseenAlerts, i }) => {
  const opacity = unseenAlerts.length - 3 > i ? 0 : 1 - (unseenAlerts.length - (i + 1)) * 0.2;
  const y = -((unseenAlerts.length - (i + 1)) * 12);
  const scale = 1 - (unseenAlerts.length - (i + 1)) * 0.04;
  return (
    <motion.div
      key={alert.id}
      initial={{
        opacity,
        y,
        scale
      }}
      animate={{
        opacity,
        y,
        scale
      }}
      exit={{ opacity: 0, scale: 1.1, y: 12 }}
      transition={{ duration: 0.8, type: "spring", bounce: 0 }}
      className="border border-solid border-[#cccccc] dark:border-[#474646ad] rounded [grid-area:1/1] flex flex-col justify-between h-[220px] bg-[#e8e8e8] dark:bg-[#2b2b2f]"
    >
      <div className="px-4 py-4 flex-1 overflow-y-auto">
        <h3 className="font-[500] m-0 pb-1">
          {alert?.asEntityType === "Board User" ? "" : `${alert?.asEntityType}: `} {defaultCellRenderer({ value: alert.entity?.name || alert?.title })}
        </h3>
        <p className="text-sm opacity-80 dark:text-[#848484]">
          {alert.alertTag === "Board Alert" ? "" : `${alert.alertTag || ALERT_TYPE_NAMES[alert.alertType]}: `} {linkifyText(defaultCellRenderer({ value: alert.description }))}
        </p>
      </div>
      <div className="text-sm flex flex-none justify-between items-center px-4 py-3 mt-1 dark:text-[#848484] opacity-80 border-[0] border-t-[1px] border-solid border-[#cccccc] dark:border-[#38373c]">
        <span>{alert.creatorBoardUserRecord?.name}</span>
        <span>{defaultCellRenderer({ value: DateHelper.formatCount(getAging(alert.alertDate), "day") })}</span>
      </div>
    </motion.div>
  );
};

const AllAlertsRead = ({ unseenAlerts }) => {
  const opacity = unseenAlerts.length > 2 ? 0 : 1 - unseenAlerts.length * 0.2;
  const y = -(unseenAlerts.length * 12);
  const scale = 1 - unseenAlerts.length * 0.04;
  const isDarkMode = document.body.dataset.theme === "dark";
  const backgroundColor = isDarkMode ? (unseenAlerts.length === 0 ? "#2b2b2f" : "") : unseenAlerts.length === 0 ? "#e8e8e8" : "";

  return (
    <motion.div
      layout
      initial={{ opacity, y, scale }}
      animate={{ opacity, y, scale, backgroundColor }}
      transition={{ duration: 0.8, type: "spring", bounce: 0 }}
      className="flex flex-col border rounded [grid-area:1/1] h-[220px]"
    >
      <div className="px-4 flex-1 flex gap-4 items-center justify-center">
        <motion.div animate={{ scale: unseenAlerts.length === 0 ? 1 : 0 }} transition={{ delay: 0.3, type: "spring", stiffness: 200 }}>
          <i className="text-4xl fa-regular fa-circle-check" />
        </motion.div>
        <motion.p
          animate={{
            opacity: unseenAlerts.length === 0 ? 1 : 0,
            y: unseenAlerts.length === 0 ? 0 : 20
          }}
          transition={{ delay: 0.6 }}
          className="text-lg font-semibold"
        >
          All alerts read
        </motion.p>
      </div>
    </motion.div>
  );
};

const SwivuBoardUsersAlerts = ({ onClose, unseenBoardAlertsOfMine = [] }) => {
  const [unseenAlerts, setUnseenAlerts] = useState(unseenBoardAlertsOfMine);

  useEffect(() => {
    setUnseenAlerts(unseenBoardAlertsOfMine);
  }, [unseenBoardAlertsOfMine]);

  const handleReadAlert = (id) => {
    const updatedAlerts = unseenAlerts.filter((alert) => {
      if (alert.id === id) {
        alert.updateSeenStatus("markSeen");
        return false;
      }
      return alert;
    });
    setUnseenAlerts(updatedAlerts);
  };

  return (
    <div className="sm:w-[650px] bg-[#f9f9f9] text-[#646464] border-2 border-solid border-[#e8e8e8] dark:border-[#464647] dark:bg-[#38373c] dark:text-[#d8d8d8] p-4 rounded-md">
      {/* Header */}
      <div className="flex items-start gap-4 flex-row pb-4">
        <div>
          <i className="text-3xl opacity-80 fa-light fa-bell" />
        </div>
        <div className="space-y-1">
          <p className="text-lg font-[500] leading-none tracking-tight text-left m-0">{pluralize(unseenAlerts.length, "Alert", "s")}</p>
          <div className="opacity-80 text-left flex">
            <AlertBoardUserNames unseenBoardAlertsOfMine={unseenBoardAlertsOfMine} />
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 pt-6 border-[0] border-t-[1px] border-solid border-[#e8e8e8] dark:border-[#464647] grid">
        <AnimatePresence>
          <AllAlertsRead unseenAlerts={unseenAlerts} />
          {unseenAlerts.map((alert, i) => (
            <AlertCard key={alert.id} alert={alert} unseenAlerts={unseenAlerts} i={i} />
          ))}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="pt-6 gap-4 flex justify-end">
        {unseenAlerts.length === 0 ? (
          <BryntumButton text="Close" cls="!rounded" onClick={() => onClose()} />
        ) : (
          <BryntumButton text="Mark as read" cls="!rounded" onClick={() => handleReadAlert(unseenAlerts[unseenAlerts.length - 1]?.id)} />
        )}
      </div>
    </div>
  );
};

export default SwivuBoardUsersAlerts;
